// Combined categorizations: which rule sets a Count metric combines and which rows it shows.
// Dependency-free on purpose (types only) so ruleInterpretation.ts, the SQL/Excel helpers and
// the editors can all import it without a cycle - same arrangement as detailRuleSetSelection.ts.
//
// Server-side mirror: ReportConfigMetricSectionDto.UsesCombinedRuleSets / CombinedRuleSetIds /
// CombinedRows, ReportPivotBuilder.CombinedRowLabel.
import type { CombinedRow, CombinedRowValue, MetricSection } from "../types";

/** Fewest rule sets the editor asks for. With one, "Categorization Rule Set" is the better fit. */
export const COMBINED_MIN_RULE_SETS = 2;

/** SQL Server identifiers and Excel cells are happiest with labels well under this. */
export const COMBINED_ROW_LABEL_MAX_LENGTH = 128;

/** True when the metric combines several rule sets (breakdownSource "ruleSets"). */
export function usesCombinedRuleSets(section: MetricSection) {
    return section.breakdownSource === "ruleSets";
}

/** The combined rule set ids, in order. Also returns the kept draft when the mode is off. */
export function combinedRuleSetIds(section: MetricSection): string[] {
    return Array.isArray(section.combinedRuleSetIds) ? section.combinedRuleSetIds : [];
}

export function combinedRows(section: MetricSection): CombinedRow[] {
    return Array.isArray(section.combinedRows) ? section.combinedRows : [];
}

/** Mirrors ReportSqlBuilder.CombinedCountOverrides, including stable ordinal aliases. */
export function combinedCountOverrides(section: MetricSection): CombinedRow[] {
    if (section.measure?.fieldId) return [];
    const seen = new Set<string>();
    return combinedRows(section).filter((row) => {
        if (row.countMode == null) return false;
        const key = JSON.stringify([row.ruleSetId, row.countMode, row.countMode === "distinct" ? row.countDistinctColumn ?? null : null]);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** The label a row gets until the user types one. Mirrors ReportPivotBuilder.CombinedRowLabel. */
export function generatedCombinedRowLabel(category: string, value: CombinedRowValue) {
    const name = (category || "").trim() || "(blank)";
    return value === "share" ? `${name} %` : name;
}

/** The row's label as it appears on the sheet. */
export function combinedRowLabel(row: CombinedRow) {
    return (row.label || "").trim() || generatedCombinedRowLabel(row.category, row.value);
}

/** Rows whose rule set is still part of the selection. */
export function activeCombinedRows(section: MetricSection): CombinedRow[] {
    const ids = new Set(combinedRuleSetIds(section));
    return combinedRows(section).filter((row) => ids.has(row.ruleSetId));
}
